import { MatTooltip } from '@angular/material/tooltip';
import { TemporaryDokumentUploadComponent } from '@app/dokument/temporary-dokument-upload/temporary-dokument-upload.component';
import { InjectUrlPipe } from '@app/shared/pipes/inject-url.pipe';
import { MarkedPipe } from '@app/shared/pipes/marked.pipe';
import { SettingPipe } from '@app/shared/pipes/setting.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { BackendService } from '../shared/services/backend.service';
import { Component, DestroyRef, ElementRef, inject, resource, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthenticationService } from '@app/core/auth/authentication.service';
import { DokumentTypSelector } from '@app/meldung-new/classes/dokument-typ-selector';
import { Dokument } from '@app/shared/classes/dokument';
import { DokumentTyp } from '@app/shared/classes/dokument-typ';
import { cloneMeldung, MeldungCmd } from '@app/shared/classes/meldung';
import { NutzungId } from '@app/shared/classes/nutzung';
import { PackungCmd } from '@app/shared/classes/packung';
import { PraeparatCmd } from '@app/shared/classes/praeparat';
import { CryptoService } from '@app/shared/services/crypto.service';
import { rxResource, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { map } from "rxjs/operators";
import { forkJoin } from "rxjs";

@Component({
  selector: 'app-meldung-new',
  templateUrl: './meldung-new.component.html',
  imports: [
    InjectUrlPipe,
    MarkedPipe,
    MatTooltip,
    ReactiveFormsModule,
    RouterLink,
    SettingPipe,
    TemporaryDokumentUploadComponent,
    TranslatePipe,
  ]
})
export class MeldungNewComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly backendService = inject(BackendService);
  private readonly cryptoService = inject(CryptoService);
  private readonly el = inject(ElementRef);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly login = this.authenticationService.getUser();
  protected readonly meldungFormSubmitted = signal(false);
  protected readonly meldungForm = signal(this.generateForm(new MeldungCmd({ praxis: this.login?.anschrift })));
  private readonly _dokumente = signal<Dokument[]>([]);

  protected readonly storedMeldung = resource({
    params: () => {
      const ciphertext = localStorage.getItem('meldung');
      return ciphertext ? { ciphertext } : undefined;
    },
    loader: ({ params }) => this.cryptoService.decrypt(params.ciphertext).then( json => MeldungCmd.fromJSON(json))
  });

  protected readonly zulassungsLaender = rxResource({ stream: () => this.backendService.listZulassungsLaender(), defaultValue: [] })
  protected readonly importLaender = rxResource({ stream: () => this.backendService.listImportLaender(), defaultValue: [] })
  protected readonly nutzungen = rxResource({ stream: () => this.backendService.listNutzung(), defaultValue: [] })
  protected readonly importGruende = rxResource({ stream: () => this.backendService.listImportGruende(), defaultValue: [] })
  private readonly dokumentTypSelector = rxResource({ stream: () => this.backendService.listDokumentTypen().pipe(map( it => new DokumentTypSelector(it) )) })

  constructor() {
    const copyId = this.route.snapshot.queryParams.copy;
    if (copyId) {
      this.loadCopy(copyId);
    }
  }

  private loadCopy(copyId: string): void {
    forkJoin({
      original: this.backendService.getMeldung(copyId),
      dokumente: this.backendService.listDokumente(copyId, true),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ original, dokumente }) => {
        const meldung = cloneMeldung(original);
        meldung.praxis = this.login?.anschrift ?? null;
        meldung.praeparat.packungen.forEach( it => it.anzahl = null );
        this.meldungForm.set(this.generateForm(meldung));
        this._dokumente.set(dokumente);
      });
  }

  private generateForm(meldung: MeldungCmd): UntypedFormGroup {
    let packungArray: UntypedFormGroup[] = [];
    meldung.praeparat.packungen.forEach( packung => {
      packungArray.push(this.buildPackung(packung.groesse ?? undefined, packung.anzahl ?? undefined));
    });
    if (packungArray.length === 0) {
      packungArray.push(this.buildPackung());
    }

    return this.formBuilder.group({
      praxis          : [meldung.praxis,                       Validators.required],
      vorname         : [meldung.vorname,                      Validators.required],
      nachname        : [meldung.nachname,                     Validators.required],
      email           : [meldung.email,                        Validators.compose([Validators.email, Validators.required])],
      telefon         : [meldung.telefon],
      grund           : [meldung.grund?.id,                    Validators.required],
      grundKommentar  : [meldung.grundKommentar],
      importLand      : [meldung.importLand?.id],
      haendlerAusland : [meldung.haendlerAusland],
      hauptIndikation : [meldung.hauptIndikation,              Validators.required],
      kommentar       : [meldung.kommentar],
      nutzung         : [meldung.praeparat.nutzung?.id,        Validators.required],
      spezies         : [meldung.praeparat.spezies,            Validators.required],
      zulassungsLand  : [meldung.praeparat.zulassungsLand?.id, Validators.required],
      zulassungsNr    : [meldung.praeparat.zulassungsNr,       Validators.required],
      praeparat       : [meldung.praeparat.name,               Validators.required],
      zulassungsNrCh  : [meldung.praeparat.zulassungsNrCh],
      anhang5         : [meldung.praeparat.anhang5,            Validators.required],
      umgewidmet      : [meldung.praeparat.umgewidmet,         Validators.required],
      bienen          : [meldung.praeparat.bienen,             Validators.required],
      immunologisch   : [meldung.praeparat.immunologisch,      Validators.required],
      hersteller      : [meldung.praeparat.hersteller],
      bestandteile    : [meldung.praeparat.bestandteile],
      tierhalter      : [meldung.praeparat.tierhalter],
      packungen       : this.formBuilder.array(packungArray),
      bestaetigung    : [false,                                Validators.requiredTrue]
    }, {validators: this.customFormValidator});
  }

  autosave(): void {
    const meldung = this.convertFormToMeldung();
    let meldungPlaintext = JSON.stringify(meldung);
    this.cryptoService.encrypt(meldungPlaintext).then(meldungCiphertext => {
      localStorage.setItem('meldung', meldungCiphertext);
    });
    this.storedMeldung.value.set(undefined);
  }

  restoreAutosave(): void {
    const storedMeldung = this.storedMeldung.value();
    if (storedMeldung) {
      this.meldungForm.set(this.generateForm(storedMeldung));
      this.storedMeldung.value.set(undefined);
      this.backendService.getDokumente(storedMeldung.neueDokumente)
        .subscribe( dokumente => this._dokumente.set(dokumente) );
    }
  }

  get packungen(): UntypedFormArray {
    return this.meldungForm().get('packungen') as UntypedFormArray;
  }

  buildPackung(groesse ?: string, anzahl ?: number): UntypedFormGroup {
    return this.formBuilder.group({
        groesse: [groesse, Validators.required],
        anzahl: [anzahl, Validators.compose([
          Validators.required, Validators.pattern(/^(?!0+$)\d+$/)
        ])]
      })
  }

  addPackung(): void {
    if (this.packungen.length < 5) {
      this.packungen.push(this.buildPackung());
    }
  }

  removePackung(): void {
    if (this.packungen.length > 1) {
      this.packungen.removeAt(this.packungen.length - 1);
    }
  }

  hasError(field: string): boolean {
    return this.meldungFormSubmitted() &&
      this.meldungForm().controls[field].invalid;
  }

  send(): void {
    if (this.meldungForm().valid) {
      //TODO: Show Spinner?
      const meldung = this.convertFormToMeldung();
      this.backendService.createMeldung(meldung)
        .subscribe(meldungId => {
          localStorage.removeItem('meldung');
          this.router.navigate(['meldung', meldungId], {state: {created: true}});
        });
    } else {
      Object.keys(this.meldungForm().controls).forEach(field => {
        this.meldungForm().get(field)?.markAsDirty({onlySelf: true});
      });
      this.meldungFormSubmitted.set(true);
      this.focusFirstInvalidField();
    }
  }

  private focusFirstInvalidField(): void {
    // Quelle: https://medium.com/javascript-everyday/how-to-scroll-to-first-invalid-control-once-a-form-has-been-submitted-eb47d9fbc6e
    this.el.nativeElement.querySelector('form .ng-invalid')?.focus();
  }

  convertFormToMeldung(): MeldungCmd {
    const formValues = this.meldungForm().value;

    return new MeldungCmd({
      uid             : formValues.uid,
      praxis          : formValues.praxis,
      vorname         : formValues.vorname,
      nachname        : formValues.nachname,
      email           : formValues.email,
      telefon         : formValues.telefon,
      grund           : this.importGruende.value().find( it => it.id == formValues.grund ),
      grundKommentar  : formValues.grundKommentar,
      importLand      : this.importLaender.value().find( it => it.id == formValues.importLand ),
      haendlerAusland : formValues.haendlerAusland,
      hauptIndikation : formValues.hauptIndikation,
      kommentar       : formValues.kommentar,
      praeparat       : new PraeparatCmd({
        nutzung        : this.nutzungen.value().find( it => it.id == formValues.nutzung ),
        spezies        : formValues.spezies,
        zulassungsLand : this.zulassungsLaender.value().find( it => it.id == formValues.zulassungsLand ),
        zulassungsNr   : formValues.zulassungsNr,
        name           : formValues.praeparat,
        zulassungsNrCh : formValues.zulassungsNrCh,
        anhang5        : formValues.anhang5,
        umgewidmet     : formValues.umgewidmet,
        bienen         : formValues.bienen,
        immunologisch  : formValues.immunologisch,
        hersteller     : formValues.hersteller,
        bestandteile   : formValues.bestandteile,
        tierhalter     : formValues.tierhalter,
        packungen      : formValues.packungen.map( (it: {groesse: string, anzahl: number}) =>
          new PackungCmd({
            groesse : it.groesse,
            anzahl  : it.anzahl
          })
        )
      }),
      neueDokumente   : this._dokumente().map( it => it.id )
    });
  }

  get isGrundKommentarPflicht(): boolean {
    const grundId = this.meldungForm().controls.grund.value;
    const grund = this.importGruende.value().find( it => it.id == grundId );
    return !!grund?.kommentarPflicht;
  }

  get showZulassungsNrCh(): boolean {
    const grundId = this.meldungForm().controls.grund.value;
    const grund = this.importGruende.value().find( it => it.id == grundId );
    return !!grund?.zulassungsNrChAnzeigen;
  }

  get showLieferEngpass(): boolean {
    const grundId = this.meldungForm().controls.grund.value;
    const grund = this.importGruende.value().find( it => it.id == grundId );
    return !!grund?.lieferEngpassAnzeigen;
  }

  get showImmunologischFelder(): boolean {
    return this.meldungForm().controls.immunologisch.value === true;
  }

  get showAlertBewilligungsPflichtig(): boolean {
    let zulassungsLand = this.zulassungsLaender.value().find( it => it.id == this.meldungForm().value.zulassungsLand );
    let nutzung = this.nutzungen.value().find( it => it.id == this.meldungForm().value.nutzung );

    return (this.meldungForm().controls.umgewidmet.value === true && nutzung?.id === NutzungId.NUTZTIER)
      || this.meldungForm().controls.anhang5.value === true
      || this.meldungForm().controls.bienen.value === true
      || (zulassungsLand && nutzung && !zulassungsLand.erlaubteNutzung.includes(nutzung.id))
      || this.meldungForm().controls.immunologisch.value === true;
  }

  get dokumentTypen(): DokumentTyp[] {
    return this.dokumentTypSelector.value()?.getTypen(this.showLieferEngpass, this.showImmunologischFelder) ?? [];
  }

  get dokumente(): Dokument[] {
    return this._dokumente();
  }

  set dokumente(dokumente: Dokument[]) {
    this._dokumente.set(dokumente);
    this.autosave();
  }

  customFormValidator: ValidatorFn = (control) => {
    const form = control as UntypedFormGroup;
    if (this.isGrundKommentarPflicht && !form.controls["grundKommentar"].value) {
      form.controls["grundKommentar"].setErrors({required: true});
    } else {
      form.controls["grundKommentar"].setErrors(null);
    }

    if (this.showZulassungsNrCh && !form.controls["zulassungsNrCh"].value) {
      form.controls["zulassungsNrCh"].setErrors({required: true});
    } else {
      form.controls["zulassungsNrCh"].setErrors(null);
    }

    form.controls["hersteller"].setErrors(null);
    form.controls["bestandteile"].setErrors(null);
    form.controls["tierhalter"].setErrors(null);
    if (this.showImmunologischFelder) {
      if (!form.controls["hersteller"].value) {
        form.controls["hersteller"].setErrors({required: true});
      }
      if (!form.controls["bestandteile"].value) {
        form.controls["bestandteile"].setErrors({required: true});
      }
      if (!form.controls["tierhalter"].value) {
        form.controls["tierhalter"].setErrors({required: true});
      }
    }

    return null;
  }
}
